import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useSnackbarStore } from "@/stores/snackbar";
import { ACCEPTANCE_ERROR_MESSAGE } from "@/constants/CommonWebConstants";
import { SNACKBAR_MESSAGE_TYPES } from "@/constants/GlobalSnackbar.js";
import { HTTP_STATUS_CODE } from "@/constants/CommonWebCodes";

export const useRatingsStore = defineStore("ratings", () => {
  let pageData = ref({});
  let validations = ref([]);
  let originalInspectionsStatus = ref(null);
  let savedWithExceptions = ref(false);
  const snackbarStore = useSnackbarStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getRatingsObject = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_RATINGS;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Ratings", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        if (e.status == HTTP_STATUS_CODE.SERVICE_UNAVAILABLE) {
          _showError(ACCEPTANCE_ERROR_MESSAGE);
          isSuccess = "Acceptance Failed";
        } else {
          isSuccess = false;
        }
      });
    return isSuccess;
  };

  const validateInspectionForRatingsAcceptance = async (brkey, inspectionId) => {
    //clear validations array
    validations.value = [];
    let url = INSPECTION_LINKS_ENDPOINT.GET_VALIDATIONS_FOR_SPECIFIC_INSPECTION.replace("{brkey}", brkey).replace(
      "{inspectionId}",
      inspectionId,
    );

    await axios
      .get(url)
      .then((resp) => {
        validations.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const _showError = (displayText) => {
    snackbarStore.showMessage({
      displayText,
      messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
      timeout: 10000,
    });
  };

  const convertToSaveInspLinkObject = (ratingsValues) => {
    return {
      brkey: ratingsValues.BRKEY,
      inspectionId: ratingsValues.INSPECTIONID,
      inspectionStatus: ratingsValues.INSPSTAT,
      inspectionReviewer: ratingsValues.inspectionReviewer,
      // Condition Ratings section
      dkRating: ratingsValues.DKRATING,
      subRating: ratingsValues.SUBRATING,
      culvRating: ratingsValues.CULVRATING,
      scourCondRating: ratingsValues.SCOUR_COND_RATING,
      underwaterRating: ratingsValues.UNDERWATER_RATING,
      nstmInspectionCondition: ratingsValues.NSTM_CONDITION,
      supRating: ratingsValues.SUPRATING,
      chanRating: ratingsValues.CHANRATING,
      chanProtRating: ratingsValues.CHAN_PROT_RATING,
      overallBearingsRating: ratingsValues.OVERALL_BEARINGS_RATING,
      overallJointsRating: ratingsValues.OVERALL_JOINTS_RATING,
      lowestConditionRating: ratingsValues.LOWEST_CONDITION_RATING,
      inspectionNotes: ratingsValues.INSPECTION_NOTES,
      // Appraisal Ratings section
      railRating: ratingsValues.RAILRATING,
      transRating: ratingsValues.TRANSRATIN,
      pierProtection: ratingsValues.PIER_PROTECTION,
      scourVulnerability: ratingsValues.SCOUR_VULNERABILITY,
      newPaint: ratingsValues.NEW_PAINT,
      paintConditionRating: ratingsValues.PAINT_COND_RATE,
      paintExtentRating: ratingsValues.PAINT_EXTENT_RATING,
      seismicVulnerability: ratingsValues.SEISMIC_VULNERABILITY,
      apprAlign: ratingsValues.APPRALIGN,
      apprSlabConditionRating: ratingsValues.APPR_SLAB_COND_RATE,
      apprRoadConditionRating: ratingsValues.APPR_ROAD_COND_RATE,
      deckWearingConditionRating: ratingsValues.DECK_WS_COND_RATE,
      scourCrit: ratingsValues.SCOURCRIT,
      scourCritCategory: ratingsValues.SCOUR_CRIT_CTGRY,
      bridgeConditionRating: ratingsValues.BRIDGE_CONDITION_RATING,
      maintenanceDeficiencyRating: ratingsValues.MAINT_DEF_RATE,
      inspectionStatusAudits: ratingsValues.inspectionStatusAudits,
      // comments and field for inspection status change
      submissionInspectionComments: ratingsValues.submissionInspectionComments,
      previousSubmissionInspectionComments: ratingsValues.previousSubmissionInspectionComments,
      newlyAcceptedInspection: ratingsValues.newlyAcceptedInspection,
    };
  };

  const convertToSaveInspModuleObject = (ratingsValues, structure) => {
    structure.inspection.inspectionStatus = ratingsValues.INSPSTAT;
    structure.inspection.inspectionReviewer = ratingsValues.inspectionReviewer;
    structure.inspection.dkRating = ratingsValues.DKRATING;
    structure.inspection.subRating = ratingsValues.SUBRATING;
    structure.inspection.culvRating = ratingsValues.CULVRATING;
    structure.userInspection.scourCondRating = ratingsValues.SCOUR_COND_RATING;
    structure.userInspection.underwaterRating = ratingsValues.UNDERWATER_RATING;
    structure.userInspection.nstmCondition = ratingsValues.NSTM_CONDITION;
    structure.inspection.supRating = ratingsValues.SUPRATING;
    structure.inspection.chanRating = ratingsValues.CHANRATING;
    structure.userInspection.chanProtRating = ratingsValues.CHAN_PROT_RATING;
    structure.userInspection.overallBearingCondRating = ratingsValues.OVERALL_BEARINGS_RATING;
    structure.userInspection.overallJointCondRating = ratingsValues.OVERALL_JOINTS_RATING;
    structure.userInspection.lowestCondition = ratingsValues.LOWEST_CONDITION_RATING;
    structure.inspection.notes = ratingsValues.INSPECTION_NOTES;

    // Appraisal Ratings section
    structure.inspection.railRating = ratingsValues.RAILRATING;
    structure.inspection.transRating = ratingsValues.TRANSRATIN;
    structure.inspection.pierprot = ratingsValues.PIER_PROTECTION;
    structure.userInspection.scourVulnerability = ratingsValues.SCOUR_VULNERABILITY;
    structure.userInspection.newPaint = ratingsValues.NEW_PAINT;
    structure.userInspection.paintCondRate = ratingsValues.PAINT_COND_RATE;
    structure.userInspection.paintExtentRate = ratingsValues.PAINT_EXTENT_RATING;
    structure.userBridge.seismicVuln = ratingsValues.SEISMIC_VULNERABILITY;
    structure.inspection.apprAlign = ratingsValues.APPRALIGN;
    structure.userInspection.apprSlabCondRate = ratingsValues.APPR_SLAB_COND_RATE;
    structure.userInspection.apprRoadCondRate = ratingsValues.APPR_ROAD_COND_RATE;
    structure.userInspection.deckWsCondRate = ratingsValues.DECK_WS_COND_RATE;
    structure.inspection.scourCritical = ratingsValues.SCOURCRIT;
    structure.inspection.scourCriticalCat = ratingsValues.SCOUR_CRIT_CTGRY;
    structure.inspection.condition = ratingsValues.BRIDGE_CONDITION_RATING;
    structure.userInspection.maintDefRate = ratingsValues.MAINT_DEF_RATE;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_RATINGS.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      INSPSTAT: structure?.inspection?.inspectionStatus,
      dkRatingDuration: structure?.inspectionConditionHist?.dkRatingDuration,
      dkRatingDurationChanges: structure?.inspectionConditionHist?.dkRatingDurationChanges,
      DKRATING: structure?.inspection?.dkRating,
      subRatingDuration: structure?.inspectionConditionHist?.subRatingDuration,
      subRatingDurationChanges: structure?.inspectionConditionHist?.subRatingDurationChanges,
      SUBRATING: structure?.inspection?.subRating,
      culvRatingDuration: structure?.inspectionConditionHist?.culvRatingDuration,
      culvRatingDurationChanges: structure?.inspectionConditionHist?.culvRatingDurationChanges,
      CULVRATING: structure?.inspection?.culvRating,
      SCOUR_COND_RATING: structure?.userInspection?.scourCondRating,
      //scour hole duration currently unavailable
      UNDERWATER_RATING: structure?.userInspection?.underwaterRating,
      //underwater rating duration currently unavailable
      NSTM_CONDITION: structure?.userInspection?.nstmCondition,
      //nstm condition duration currently unavailable
      supRatingDuration: structure?.inspectionConditionHist?.supRatingDuration,
      supRatingDurationChanges: structure?.inspectionConditionHist?.supRatingDurationChanges,
      SUPRATING: structure?.inspection?.supRating,
      chanRatingDuration: structure?.inspectionConditionHist?.chanRatingDuration,
      chanRatingDurationChanges: structure?.inspectionConditionHist?.chanRatingDurationChanges,
      CHANRATING: structure?.inspection?.chanRating,
      CHAN_PROT_RATING: structure?.userInspection.chanProtRating,
      //channel protection rating duration not currently unavailable
      OVERALL_BEARINGS_RATING: structure?.userInspection.overallBearingCondRating,
      //overall bearings duration not currently unavailable
      OVERALL_JOINTS_RATING: structure?.userInspection.overallJointCondRating,
      //overall joints duration not currently unavailable
      LOWEST_CONDITION_RATING: structure?.userInspection?.lowestCondition,
      INSPECTION_NOTES: structure?.inspection?.notes,

      // Appraisal Ratings section
      railRatingDuration: structure?.inspectionConditionHist?.railRatingDuration,
      railRatingDurationChanges: structure?.inspectionConditionHist?.railRatingDurationChanges,
      RAILRATING: structure?.inspection?.railRating,
      transRatingDuration: structure?.inspectionConditionHist?.transRatingDuration,
      transRatingDurationChanges: structure?.inspectionConditionHist?.transRatingDurationChanges,
      TRANSRATIN: structure?.inspection?.transRating,
      PIER_PROTECTION: structure?.inspection?.pierprot,
      //pier protection duration currently unavailable
      SCOUR_VULNERABILITY: structure?.userInspection?.scourVulnerability,
      //scour vulnerability duration currently unavailable
      NEW_PAINT: structure?.userInspection?.newPaint,
      paintCondRateDuration: structure?.inspectionConditionHist?.paintCondRateDuration,
      paintCondRateDurationChanges: structure?.inspectionConditionHist?.paintCondRateDurationChanges,
      PAINT_COND_RATE: structure?.userInspection?.paintCondRate,
      paintExtentRateDuration: structure?.inspectionConditionHist?.paintExtentRateDuration,
      paintExtentRateDurationChanges: structure?.inspectionConditionHist?.paintExtentRateDurationChanges,
      PAINT_EXTENT_RATING: structure?.userInspection?.paintExtentRate,
      SEISMIC_VULNERABILITY: structure?.userBridge?.seismicVuln,
      // seismic vulnerability duration currently unavailable
      apprAlignDuration: structure?.inspectionConditionHist?.apprAlignDuration,
      apprAlignDurationChanges: structure?.inspectionConditionHist?.apprSlabCondRateDurationChanges,
      APPRALIGN: structure?.inspection?.apprAlign,
      apprSlabCondRateDuration: structure?.inspectionConditionHist?.apprSlabCondRateDuration,
      apprSlabCondRateDurationChanges: structure?.inspectionConditionHist?.apprSlabCondRateDurationChanges,
      APPR_SLAB_COND_RATE: structure?.userInspection?.apprSlabCondRate,
      apprRoadCondRateDuration: structure?.inspectionConditionHist?.apprRoadCondRateDuration,
      apprRoadCondRateDurationChanges: structure?.inspectionConditionHist?.apprRoadCondRateDurationChanges,
      APPR_ROAD_COND_RATE: structure?.userInspection?.apprRoadCondRate,
      deckWsCondRateDuration: structure?.inspectionConditionHist?.deckWsCondRateDuration,
      deckWsCondRateDurationChanges: structure?.inspectionConditionHist?.deckWsCondRateDurationChanges,
      DECK_WS_COND_RATE: structure?.userInspection?.deckWsCondRate,
      scourCriticalDuration: structure?.inspectionConditionHist?.scourCriticalDuration,
      scourCriticalDurationChanges: structure?.inspectionConditionHist?.scourCriticalDurationChanges,
      SCOURCRIT: structure?.inspection?.scourCritical,
      SCOUR_CRIT_CTGRY: structure?.inspection?.scourCriticalCat,
      conditionDuration: structure?.inspectionConditionHist?.conditionDuration,
      conditionDurationChanges: structure?.inspectionConditionHist?.conditionDurationChanges,
      BRIDGE_CONDITION_RATING: structure?.inspection?.condition,
      MAINT_DEF_RATE: structure?.userInspection?.maintDefRate,
      MAIN_FC_GROUP_NUM: structure?.userBridge?.mainFCGroupNum,
      APPR_FC_GROUP_NUM: structure?.userBridge?.apprFCGroupNum,
    };
  };

  const convertToInspLinkObject = (response) => {
    originalInspectionsStatus.value = response.inspectionStatus;
    savedWithExceptions.value = false;
    pageData.value = {
      BRKEY: response.brkey,
      INSPECTIONID: response.inspectionId,
      INSPSTAT: response.inspectionStatus,
      inspectionReviewer: response.inspectionReviewer,

      // ***still need the Key field comparison***

      // Condition Ratings section
      dkRatingDuration: response.dkRatingDuration,
      dkRatingDurationChanges: "",
      DKRATING: response.dkRating,
      subRatingDuration: response.subRatingDuration,
      subRatingDurationChanges: "",
      SUBRATING: response.subRating,
      culvRatingDuration: response.culvRatingDuration,
      culvRatingDurationChanges: "",
      CULVRATING: response.culvRating,
      SCOUR_COND_RATING: response.scourCondRating,
      SCOUR_HOLE: response.scourHole,
      //scour hole duration currently unavailable
      UNDERWATER_RATING: response.underwaterRating,
      //underwater rating duration currently unavailable
      NSTM_CONDITION: response.nstmInspectionCondition,
      //nstm condition duration currently unavailable
      supRatingDuration: response.supRatingDuration,
      supRatingDurationChanges: "",
      SUPRATING: response.supRating,
      chanRatingDuration: response.chanRatingDuration,
      chanRatingDurationChanges: "",
      CHANRATING: response.chanRating,
      CHAN_PROT_RATING: response.chanProtRating,
      //channel protection rating duration not currently unavailable
      OVERALL_BEARINGS_RATING: response.overallBearingsRating,
      //overall bearings duration not currently unavailable
      OVERALL_JOINTS_RATING: response.overallJointsRating,
      //overall joints duration not currently unavailable
      LOWEST_CONDITION_RATING: response.lowestConditionRating,
      INSPECTION_NOTES: response.inspectionNotes,

      // Appraisal Ratings section
      railRatingDuration: response.railRatingDuration,
      railRatingDurationChanges: "",
      RAILRATING: response.railRating,
      transRatingDuration: response.transRatingDuration,
      transRatingDurationChanges: "",
      TRANSRATIN: response.transRating,
      PIER_PROTECTION: response.pierProtection,
      //pier protection duration currently unavailable
      SCOUR_VULNERABILITY: response.scourVulnerability,
      //scour vulnerability duration currently unavailable
      NEW_PAINT: response.newPaint,
      paintCondRateDuration: response.paintCondRateDuration,
      paintCondRateDurationChanges: "",
      PAINT_COND_RATE: response.paintConditionRating,
      paintExtentRateDuration: response.paintExtentRateDuration,
      paintExtentRateDurationChanges: "",
      PAINT_EXTENT_RATING: response.paintExtentRating,
      SEISMIC_VULNERABILITY: response.seismicVulnerability,
      // seismic vulnerability duration currently unavailable
      apprAlignDuration: response.apprAlignDuration,
      apprAlignDurationChanges: "",
      APPRALIGN: response.apprAlign,
      apprSlabCondRateDuration: response.apprSlabCondRateDuration,
      apprSlabCondRateDurationChanges: "",
      APPR_SLAB_COND_RATE: response.apprSlabConditionRating,
      apprRoadCondRateDuration: response.apprRoadCondRateDuration,
      apprRoadCondRateDurationChanges: "",
      APPR_ROAD_COND_RATE: response.apprRoadConditionRating,
      deckWsCondRateDuration: response.deckWsCondRateDuration,
      deckWsCondRateDurationChanges: "",
      DECK_WS_COND_RATE: response.deckWearingConditionRating,
      scourCriticalDuration: response.scourCriticalDuration,
      scourCriticalDurationChanges: "",
      SCOURCRIT: response.scourCrit,
      SCOUR_CRIT_CTGRY: response.scourCritCategory,
      conditionDuration: response.bridgeConditionRatingDuration,
      conditionDurationChanges: "",
      BRIDGE_CONDITION_RATING: response.bridgeConditionRating,
      MAINT_DEF_RATE: response.maintenanceDeficiencyRating,
      MAIN_FC_GROUP_NUM: response.mainFCGroupNum,
      APPR_FC_GROUP_NUM: response.apprFCGroupNum,
      inspectionStatusAudits: response.inspectionStatusAudits,
      // comments and field for inspection status change
      submissionInspectionComments: response.submissionInspectionComments,
      previousSubmissionInspectionComments: response.previousSubmissionInspectionComments,
      newlyAcceptedInspection: false,
    };
  };

  return {
    savedWithExceptions,
    pageData,
    validations,
    validateInspectionForRatingsAcceptance,
    originalInspectionsStatus,
    getRatingsObject,
    saveData,
    loadDataObject,
  };
});
